import {
  Box,
  InputLabel,
  TextField,
  styled,
  FormControlLabel,
  FormGroup,
  Stack,
  Paper,
  List,
  Typography,
} from '@mui/material';
import { iconClasses } from '@mui/material/Icon';
import { CancelIcon, LightbulbIcon } from 'ui/SvgIcon';
import SearchIcon from 'ui/SvgIcon/icons/Search';

export const StyledContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'noGap',
})<{ noGap?: boolean }>(({ theme: { spacing, palette, breakpoints }, noGap }) => ({
  position: 'absolute',
  width: '100%',
  maxHeight: '100%',
  height: '100%',
  maxWidth: spacing(80),
  overflow: 'hidden',
  alignItems: 'center',
  justifyContent: 'center',
  color: palette.cvi.neutrals[80],
  gap: noGap ? 0 : spacing(4),
  [breakpoints.up('md')]: {
    gap: noGap ? 0 : spacing(6),
  },
}));

export const StyledWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasFrame' && prop !== 'fillHeight',
})<{ hasFrame?: boolean; fillHeight?: boolean }>(
  ({ theme: { spacing, palette, breakpoints }, hasFrame, fillHeight }) => ({
    alignItems: 'center',
    justifyContent: fillHeight ? 'flex-start' : 'center',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    minHeight: 0,
    height: fillHeight ? '100%' : 'fit-content',
    flexDirection: 'column',
    gap: spacing(4),
    [breakpoints.up('md')]: {
      gap: spacing(6),
    },
    ...(hasFrame && {
      width: 'unset',
      maxWidth: '100%',
      border: `1px solid ${palette.cvi.neutrals[50]}`,
      borderRadius: spacing(1),
    }),
  }),
);

export const StyledReviewWrapper = styled(Stack)(({ theme: { spacing } }) => ({
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  gap: spacing(3),
}));

export const StyledStepHeader = styled(Typography)(({ theme: { breakpoints } }) => ({
  textAlign: 'center',
  fontWeight: 700,
  fontSize: 18,
  [breakpoints.up('md')]: {
    fontSize: 23,
  },
}));

export const StyledSearchIcon = styled(SearchIcon)(({ theme: { spacing, palette } }) => ({
  color: palette.cvi.neutrals[70],
  height: spacing(2.5),
  width: spacing(2.5),
}));

export const StyledInputLabel = styled(InputLabel)(({ theme: { palette, spacing } }) => ({
  color: palette.cvi.neutrals[70],
  lineHeight: spacing(3),
}));

export const StyledInput = styled(TextField)(({ theme: { spacing } }) => ({
  width: '100%',
  input: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-root': {
    gap: spacing(1),
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: spacing(1),
  },
}));

export const StyledAlertBox = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing(1),
}));

export const StyledLightbulbIcon = styled(LightbulbIcon)(({ theme: { spacing, palette } }) => ({
  width: spacing(3),
  height: spacing(4),
  '& path': {
    fill: palette.cvi.blue[50],
  },
}));

export const StyledKongaTypography = styled(Typography)(({ theme: { palette, spacing } }) => ({
  fontFamily: 'KongaPro',
  textAlign: 'center',
  color: palette.cvi.primary[30],
  fontSize: spacing(6),
  lineHeight: spacing(7),
}));

export const StyledContent = styled(Typography)(({ theme: { palette } }) => ({
  color: palette.cvi.neutrals[10],
  fontWeight: 700,
  textAlign: 'center',
}));

export const StyledCancelIcon = styled(CancelIcon)(({ theme: { spacing } }) => ({
  [`&.${iconClasses.fontSizeSmall}`]: {
    fontSize: spacing(2.5),
  },
}));

export const StyledFirstOrLastContainer = styled(Stack)(({ theme: { spacing } }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  minHeight: 0,
  gap: spacing(2),
}));

export const StyledFirstOrLastBox = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const StyledStepContent = styled(Stack)(({ theme: { spacing, breakpoints } }) => ({
  gap: spacing(3),
  alignItems: 'center',
  width: '100%',
  maxWidth: spacing(60),
  [breakpoints.up('md')]: {
    width: spacing(60),
  },
}));

export const StyledOverlayContent = styled(StyledStepContent)(({ theme: { spacing } }) => ({
  position: 'relative',
  margin: 'auto',
  minWidth: '100%',
  gap: spacing(6),
  overflow: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const StyledOverlay = styled(Box)(({ theme: { spacing } }) => ({
  height: spacing(5),
  position: 'absolute',
  zIndex: 50,
  pointerEvents: 'none',
  width: '100%',
  background: 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 95%)',
}));

export const BottomOverlay = styled(StyledOverlay)(({ theme: { spacing } }) => ({
  bottom: spacing(-0.1),
  transform: 'rotate(180deg)',
}));

export const StyledFormGroup = styled(FormGroup)(({ theme: { palette, spacing } }) => ({
  width: '100%',
  border: `1.5px solid ${palette.cvi.neutrals[30]}`,
  padding: spacing(2),
  borderRadius: spacing(0.5),
}));

export const StyledFormControlLabel = styled(FormControlLabel)(({ theme: { spacing } }) => ({
  display: 'flex',
  width: '100%',
  gap: spacing(1),
  '& span': {
    margin: 0,
  },
}));

export const StyledLink = styled('a')(({ theme: { palette, spacing } }) => ({
  color: palette.cvi.primary[30],
  marginTop: spacing(1.5),
  fontWeight: 500,
}));

export const StyledCustomWrapper = styled(Stack)(({ theme: { spacing, breakpoints } }) => ({
  justifyContent: 'flex-start',
  height: 'fit-content',
  width: '100%',
  [breakpoints.up('md')]: {
    width: 'unset',
    minWidth: '100%',
    maxWidth: spacing(80),
  },
}));

export const StyledContactFormConfirmMapBox = styled(Box)(({ theme: { spacing } }) => ({
  overflow: 'hidden',
  width: '100%',
  height: spacing(12),
  borderRadius: spacing(1, 1, 0, 0),
}));

export const StyledDropdownWrapper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'fixed',
})<{ fixed: boolean }>(({ theme: { spacing, zIndex }, fixed }) => ({
  display: 'flex',
  padding: spacing(2),
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: spacing(1),
  alignSelf: 'stretch',
  borderRadius: spacing(1.5),
  position: fixed ? 'fixed' : 'absolute',
  zIndex: zIndex.drawer,
  overflow: 'auto',
  width: '100%',
  overflowY: 'hidden',
}));

export const StyledList = styled(List)({
  padding: 0,
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});
